import { Link } from 'react-router-dom';
import { Home, Layers, BookOpen, Phone } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';

const recoveryCards = [
  { title: 'Homepage', description: 'Start from the beginning. See everything Flynaut offers.', href: '/', icon: Home },
  { title: 'Services', description: 'Explore our five technology practices.', href: '/services/application-development', icon: Layers },
  { title: 'Case Studies', description: 'See real work and real results.', href: '/case-studies', icon: BookOpen },
  { title: 'Contact', description: 'Talk to a human. We are friendly.', href: '/contact', icon: Phone },
];

export default function NotFoundPage() {
  return (
    <div className="bg-white min-h-screen">
      <SEOHead
        title="Page Not Found | Flynaut"
        description="The page you are looking for does not exist."
        canonical="/404"
        noIndex
      />
      <a href="#main-content" className="skip-nav">Skip to main content</a>
      <Navbar />
      <main id="main-content" className="relative overflow-hidden">
        <div className="max-w-[600px] mx-auto px-6 pt-44 pb-24 text-center relative z-10">
          {/* Faint paper airplane background */}
          <svg
            viewBox="0 0 200 200"
            className="absolute top-24 left-1/2 -translate-x-1/2 w-[200px] h-[200px] text-navy-100 rotate-[-15deg] pointer-events-none select-none"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M20 180 L180 100 L20 20 L60 100 Z" opacity="0.5" />
            <path d="M60 100 L180 100 L80 130 Z" opacity="0.35" />
          </svg>

          <h1
            data-testid="404-headline"
            className="text-navy-800 font-bold tracking-tight mb-4 relative"
            style={{ fontSize: 'clamp(1.75rem, 1.25rem + 2vw, 2.25rem)', lineHeight: 1.15 }}
          >
            This page took a wrong turn.
          </h1>
          <p data-testid="404-subheadline" className="text-navy-600 text-base lg:text-lg leading-relaxed mb-12 relative">
            The page you are looking for does not exist, was moved, or is taking the day off. Here are some places that do exist:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 relative">
            {recoveryCards.map((card) => {
              const Icon = card.icon;
              return (
                <Link
                  key={card.title}
                  to={card.href}
                  data-testid={`404-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="group bg-white rounded-xl border border-navy-100 p-5 text-left hover:border-orange-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                      <Icon size={16} className="text-orange-500" />
                    </div>
                    <div>
                      <h2 className="text-navy-800 font-bold text-sm mb-1 group-hover:text-orange-600 transition-colors">{card.title}</h2>
                      <p className="text-navy-500 text-xs leading-relaxed">{card.description}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
